import React, { useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import ReverseAuctionConsumer from "./ReverseAuctionConsumer";
import ReverseAuctionGenerator from "./ReverseAuctionGenerator";
import AuctionPageDashboard from "../AuctionPageDashboard";
import {
  activeView, // Assuming this is an action
  auctionBidders,
  auctionGet,
} from "../../../../../Redux/Slices/auctionSlice";
import Pages_Coming_Soon from "../../../../OpenAccess/ComingSoon/Pages_Coming_Soon";

// Define notification keys outside the component to prevent re-creation on every render
const REFRESH_NOTIFICATION_KEYS = [
  "BSRANotifyInvitedGenerator",
  "BSRANotifyConsumerTenderCreated",
  "BSRANotifyConsumerTenderTerminated",
  "BSRANotifyConsumerTenderUpdate",
  "BSRANotifyConsumerBidCreated",
  "BSRANotifyBidWinnerGenerator",
  "BSRANotifyBidRejectedGenerator",
  "BSRANotifyAmountChangeRequestGenerator",
  "BSRANotifyAmountChangeResponseConsumer",
  "BSRANotifyConsumerBidUpdated",
  "BSRANotifyBidWinnerGeneratorStageTwo",
  "BSRANotifyBidWinnerGeneratorStageOne",
  "BSRANotifyBidRejectedGeneratorStageOne",
  "BSRANotifyBidRejectedGeneratorStageTwo",
  "BSRANotifyGeneratorBidCreated",
  "BSRANotifyConsumerTenderActivation",
  "BSRANotifyGeneratorBidUpdated",
  "BSRANotifyConsumerTenderStart",
  "BSRANotifyConsumerTenderEnd",
  "BSRANotifyGeneratorTenderStart",
  "BSRANotifyGeneratorTenderEnd",
  "BSRANotifyConsumerBidStart",
  "BSRANotifyConsumerBidEnd",
  "BSRANotifyGeneratorBidStart",
  "BSRANotifyGeneratorBidEnd",
  "BSRANotifyConsumerStartAuction",
  "BSRANotifyConsumerEndAuction",
  "BSRANotifyGeneratorStartAuction",
  "BSRANotifyGeneratorEndAuction",
  "BSRANotifyTenderActivationAllGenerator",
  "BSRANotifyAmountChangeRequestConsumer",
  "BSRANotifyAmountChangeResponseGenerator",
  "BSRANotifyBidWinnerConsumer",
  "BSRAEmdBlockedNotification",
  "BSRAEmdReleasedNotification",
  "BSRAConsumerRaFeeBlockedNotification",
  "BSRAGenerateConsent",
  "BSRFQNotifyConsumerTenderCreated",
];

const ReverseAuction = () => {
  const location = useLocation();
  const dispatch = useDispatch();

  // 1. Safe Access to Role
  const role = useSelector((state) =>
    (state?.persistedReducer?.user?.activeRole || "").toLowerCase(),
  );

  const notify = useSelector((state) => state?.notificationSlice?.latest);
  const activeState = useSelector((state) => state?.auctionSlice?.activeView);

  const rfq = location?.pathname === "/rfq";

  // 2. Map State to API Section clearly
  const getSectionParams = useCallback((viewState) => {
    const map = {
      All: "All",
      Drafted: "Drafted",
      IPO: "IPO",
      "Live RA": "live_ra",
      Documentation: "TERMS",
      "Ongoing Contract": "CLOSED",
    };
    return map[viewState] || "All";
  }, []);

  // 3. API Call Function
  const getAuction = useCallback(() => {
    // Calculate the section needed for the API
    const apiSection = getSectionParams(activeState);

    const payload = {
      section: apiSection,
      tenderType: rfq ? "CB" : "RA",
    };

    if (role === "consumer") {
      dispatch(auctionGet(payload));
    } else if (role === "generator") {
      dispatch(auctionBidders(payload));
    }

    // CRITICAL FIX: Removed dispatch(activeView(...)) from here.
    // Dispatching an action that updates 'activeState' inside a function
    // that depends on 'activeState' causes an infinite loop.
  }, [activeState, role, rfq, dispatch, getSectionParams]);

  // 4. Initial Load & dependency on role/view change
  useEffect(() => {
    getAuction();
  }, [getAuction]);

  // 5. Notification Listener
  useEffect(() => {
    if (notify?.key && REFRESH_NOTIFICATION_KEYS.includes(notify.key)) {
      console.debug("Refreshing auction due to notification:", notify.key);
      getAuction();
    }
  }, [notify, getAuction]);

  // 6. Ensure default View State (Optional safety)
  // If activeState is null on mount, set it to "All"
  useEffect(() => {
    if (!activeState) {
      dispatch(activeView("All"));
    }
  }, [activeState, dispatch]);

  return (
    <>
      {process.env.REACT_APP_ENV === "qa" ||
      process.env.REACT_APP_ENV === "stage" ? (
        role === "consumer" ? (
          <ReverseAuctionConsumer rfq={rfq} />
        ) : role === "generator" ? (
          <ReverseAuctionGenerator rfq={rfq} />
        ) : (
          <AuctionPageDashboard />
        )
      ) : (
        <Pages_Coming_Soon />
      )}
    </>
  );
};

export default ReverseAuction;
